import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import Swal from 'sweetalert2';
import { ConfigService } from './config.service';

export interface PushSubscriptionData {
  endpoint: string;
  keys: { p256dh: string; auth: string };
}

/**
 * Tipos de notificación que un admin puede silenciar desde el panel de
 * preferencias (ver PLAN_notificaciones-estandarizado.md). "plan-derivado" y
 * "prueba-lista" quedan afuera a propósito, no aplican a un panel de admin.
 */
export const MUTABLE_NOTIFICATION_TYPES = ['nueva-alerta', 'plantilla-completada'] as const;
export type MutableNotificationType = (typeof MUTABLE_NOTIFICATION_TYPES)[number];

export interface PushSubscriptionPreferences {
  subjectType: 'admin' | 'candidate';
  mutedTypes: string[];
}

// Envelope de exito segun api-best-practices.md: { data, metadata } -- el
// backend nunca devuelve el recurso "pelado" en el body.
interface EnvelopeSuccess<T> {
  data: T;
  metadata: { requestId: string; timestamp: string };
}

const STORAGE_KEY = 'ia-eva-push-subscription';

@Injectable({ providedIn: 'root' })
export class PushNotificationService {
  constructor(private config: ConfigService, private http: HttpClient) {}

  async register(): Promise<void> {
    if (!('serviceWorker' in navigator)) {
      return;
    }
    try {
      await navigator.serviceWorker.register('/push-sw.js');
    } catch (err) {
      console.error('[PushNotificationService] Error registrando Service Worker', err);
    }
  }

  // 'admin': admin/valorador (navegate.component.ts) -- puede mencionar los
  // 4 tipos de aviso porque le llegan todos. 'candidate' (process.component.ts,
  // quiz.component.ts): solo le llega el Caso 3 (su propia prueba lista), asi
  // que el primer no debe mencionar alertas ni analisis de otros -- esa es
  // informacion interna de admin que el candidato evaluado no necesita saber.
  async subscribe(audience: 'admin' | 'candidate' = 'admin'): Promise<PushSubscriptionData | null> {
    // Chequeo por valor, no por presencia de la propiedad ("in"): un browser
    // sin soporte real puede tener la propiedad definida mientras es undefined
    // (asi lo simulan tambien los tests), y con "in" el guard no cortaria --
    // seguiria hasta el priming/prompt de permiso sin poder completar nada.
    if (!('serviceWorker' in navigator) || !window.PushManager || !window.Notification) {
      return null;
    }

    try {
      // Priming: si el navegador todavia no pregunto nada ('default'), mostramos
      // un mensaje propio explicando por que conviene aceptar ANTES del prompt
      // nativo del navegador. Si el usuario dice que no aca, el permiso nativo
      // queda intacto en 'default' (no se llega a pedir), asi que se le puede
      // volver a preguntar mas adelante -- a diferencia de si rechaza el prompt
      // nativo directamente, que queda 'denied' de forma persistente.
      if (Notification.permission === 'default') {
        const wantsToEnable = await this.showPermissionPrimer(audience);
        if (!wantsToEnable) {
          return null;
        }
      }

      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        return null;
      }

      const vapidPublicKey = this.config.vapidPublicKey;
      if (!vapidPublicKey) {
        console.error('[PushNotificationService] VAPID public key no configurada');
        return null;
      }

      const registration = await navigator.serviceWorker.ready;
      const rawSubscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: this.urlBase64ToUint8Array(vapidPublicKey),
      });

      const subscription = this.toPushSubscriptionData(rawSubscription);
      if (!subscription) {
        return null;
      }

      localStorage.setItem(STORAGE_KEY, JSON.stringify(subscription));

      // Registro estandarizado (ver PLAN_notificaciones-estandarizado.md): 1
      // sola ruta compartida, se re-registra en cada login para mantener la
      // fila fresca (upsert) — best-effort, un fallo acá no debe romper el
      // flujo que llamó a subscribe().
      await this.registerWithBackend(subscription);

      return subscription;
    } catch (err) {
      console.error('[PushNotificationService] Error obteniendo suscripción push', err);
      return null;
    }
  }

  /**
   * Lee el estado actual de preferencias del admin autenticado (sin escribir
   * nada — cuerpo vacío, ver contrato de register-push-subscription-api).
   * Devuelve `mutedTypes: []` cuando el admin todavía no tiene fila (nunca
   * aceptó push): por defecto todas las notificaciones quedan activadas.
   */
  async getPreferences(): Promise<PushSubscriptionPreferences> {
    try {
      const res = await firstValueFrom(
        this.http.post<EnvelopeSuccess<PushSubscriptionPreferences>>(this.config.urlPushSubscriptions, {}),
      );
      return res.data;
    } catch (err: any) {
      if (err?.status === 404) {
        return { subjectType: 'admin', mutedTypes: [] };
      }
      throw err;
    }
  }

  async updatePreferences(mutedTypes: string[]): Promise<PushSubscriptionPreferences> {
    const res = await firstValueFrom(
      this.http.post<EnvelopeSuccess<PushSubscriptionPreferences>>(this.config.urlPushSubscriptions, { mutedTypes }),
    );
    return res.data;
  }

  private async registerWithBackend(subscription: PushSubscriptionData): Promise<void> {
    try {
      await firstValueFrom(
        this.http.post(this.config.urlPushSubscriptions, { pushSubscription: subscription }),
      );
    } catch (err) {
      console.warn('[PushNotificationService] Error registrando suscripción en el backend', err);
    }
  }

  private async showPermissionPrimer(audience: 'admin' | 'candidate'): Promise<boolean> {
    const text = audience === 'candidate'
      ? 'Te avisaremos apenas tu prueba esté lista para continuar, sin que tengas que estar recargando la página. Haz clic en \'Permitir\' en la alerta de tu navegador para continuar.'
      : 'Te avisaremos apenas haya novedades (un análisis terminado, una alerta nueva, una prueba lista) sin que tengas que estar recargando la página. Haz clic en \'Permitir\' en la alerta de tu navegador para continuar.';
    const result = await Swal.fire({
      title: 'Activar notificaciones',
      text,
      icon: 'info',
      confirmButtonText: 'Activar notificaciones',
      showCancelButton: true,
      cancelButtonText: 'Ahora no',
    });
    return result.isConfirmed;
  }

  getSubscription(): PushSubscriptionData | null {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) {
        return null;
      }
      const parsed = JSON.parse(raw);
      if (!parsed?.endpoint || !parsed?.keys?.p256dh || !parsed?.keys?.auth) {
        return null;
      }
      return parsed as PushSubscriptionData;
    } catch {
      return null;
    }
  }

  private toPushSubscriptionData(subscription: PushSubscription): PushSubscriptionData | null {
    try {
      const json = subscription.toJSON();
      if (json.endpoint && json.keys?.['p256dh'] && json.keys?.['auth']) {
        return {
          endpoint: json.endpoint,
          keys: { p256dh: json.keys['p256dh'], auth: json.keys['auth'] },
        };
      }
    } catch {
      // toJSON no disponible en algun navegador -- armamos a mano abajo.
    }

    const p256dhKey = subscription.getKey('p256dh');
    const authKey = subscription.getKey('auth');
    if (!p256dhKey || !authKey) {
      return null;
    }

    return {
      endpoint: subscription.endpoint,
      keys: {
        p256dh: this.arrayBufferToBase64(p256dhKey),
        auth: this.arrayBufferToBase64(authKey),
      },
    };
  }

  private arrayBufferToBase64(buffer: ArrayBuffer): string {
    let binary = '';
    const bytes = new Uint8Array(buffer);
    bytes.forEach((b) => (binary += String.fromCharCode(b)));
    return btoa(binary);
  }

  private urlBase64ToUint8Array(base64String: string): Uint8Array {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
    const rawData = atob(base64);
    const outputArray = new Uint8Array(rawData.length);
    for (let i = 0; i < rawData.length; i++) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  }
}
